import { Component, Element, h, Host, Listen, State, Watch } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import {
    calculateMenuItemOffsetLeft,
    calculateMenuItemsTotalWidth,
    findMenuElement,
    findMenuItemElements,
    findMenuItemWithName,
    getMenuItemLabel
} from "../header-menu/utils";

const baseClass = "uic-header";

/**
 * @slot home - Home `uic-header-menu` element, menu should only have a single item
 * @slot menu-left - Left `uic-header-menu` element
 * @slot menu-right - Right `uic-header-menu` element
 * @slot flyout - Container for `uic-flyout` elements
 */
@Component({
    tag: "uic-header",
    styleUrl: "header.scss"
})
export class Header {
    private mainMenuContainer: HTMLDivElement = null;
    private subMenuContainer: HTMLDivElement = null;

    @Element() host: HTMLUicHeaderElement;

    /** Flag is `true` the menu exceeds the available space */
    @State() menuCropped = false;
    /** Flag is `true` if the menu items should be rendered in a submenu */
    @State() menuExpanded = false;
    /** Flag is `true` if the menu is cropped on the left side */
    @State() isCroppedLeft = false;
    /** Flag is `true` if the menu is cropped on the right side */
    @State() isCroppedRight = false;

    @Watch("menuCropped")
    @Watch("menuExpanded")
    updateMenuExpaned() {
        if (this.menuCropped && this.menuExpanded) {
            this.focusSubmenuItemDebounced();
        }
    }

    @Listen("resize", { target: "window" })
    handleResize() {
        this.updateMenusDebounced();
    }

    @Listen("uicMenuChange")
    handleMenuChange({ detail: { name: menuName, current } }) {
        if (menuName === "expand") {
            this.menuExpanded = current === "expand";
        } else {
            this.menuExpanded = false;
            const menu = findMenuElement(this.mainMenuContainer, menuName);
            if (menu !== null && menu.name === menuName) {
                this.updateCroppedMenuScrollPos(current);
            }
        }
    }

    private updateMenus = () => {
        if (this.mainMenuContainer === null) {
            return;
        }
        const menuLeftWidth = calculateMenuItemsTotalWidth(this.mainMenuContainer);
        this.menuCropped = menuLeftWidth > this.mainMenuContainer.offsetWidth;
        if (this.menuCropped) {
            this.updateCroppedMenuScrollPos(null);
        }
    };

    private updateMenusDebounced = debounce(this.updateMenus, 50);

    private focusSubmenuItem = () => {
        let found = false;
        findMenuItemElements(this.subMenuContainer).forEach((el) => {
            if (!found && el.selected) {
                found = true;
                el.focus();
            }
        });
    };

    private focusSubmenuItemDebounced = debounce(this.focusSubmenuItem, 50);

    private updateCroppedMenuScrollPos = (current: string = null) => {
        if (!this.menuCropped) {
            return;
        }
        if (current === null) {
            const mainMenu = findMenuElement(this.mainMenuContainer);
            current = mainMenu !== null ? mainMenu.current : null;
        }
        if (current === null) {
            return;
        }
        const selectedMenuItem = findMenuItemWithName(this.mainMenuContainer, current);
        if (selectedMenuItem === null) {
            return;
        }

        const maxScrollLeft = this.mainMenuContainer.scrollWidth - this.mainMenuContainer.clientWidth;
        const availableSpace = Math.max(0, this.mainMenuContainer.offsetWidth - selectedMenuItem.offsetWidth);
        const offsetX: number = Math.round(calculateMenuItemOffsetLeft(this.mainMenuContainer, current) - 0.5 * availableSpace);
        this.mainMenuContainer.scrollTo(offsetX, 0);
        this.isCroppedLeft = offsetX > 20;
        this.isCroppedRight = offsetX < maxScrollLeft - 20;
    };

    private renderSubmenu() {
        const menu = findMenuElement(this.mainMenuContainer);
        if (menu === null) {
            return null;
        }
        const menuItems = findMenuItemElements(menu);
        if (menuItems.length === 0) {
            return null;
        }
        return (
            <uic-header-menu name={menu.name} current={menu.current} variant='secondary' vertical={true}>
                {menuItems.map((item) => (
                    <uic-header-menu-item key={item.name} name={item.name} icon={item.icon} label={getMenuItemLabel(item)} />
                ))}
            </uic-header-menu>
        );
    }

    componentDidLoad() {
        this.updateMenus();
    }

    render() {
        const { menuCropped, menuExpanded, isCroppedLeft, isCroppedRight } = this;
        return (
            <Host class={`${baseClass}`}>
                <nav class={`${baseClass}__nav`}>
                    <div
                        class={{
                            [`${baseClass}__home`]: true
                        }}
                    >
                        <slot name='home' />
                    </div>
                    <div
                        class={{
                            [`${baseClass}__menu-wrapper`]: true,
                            [`${baseClass}__menu-wrapper--cropped`]: menuCropped,
                            [`${baseClass}__menu-wrapper--cropped-left`]: menuCropped && isCroppedLeft,
                            [`${baseClass}__menu-wrapper--cropped-right`]: menuCropped && isCroppedRight
                        }}
                    >
                        <div class={`${baseClass}__menu-left`} ref={(el) => (this.mainMenuContainer = el)}>
                            <slot name='menu-left' />
                        </div>
                    </div>
                    {menuCropped && (
                        <uic-header-menu
                            class={`${baseClass}__menu-expand`}
                            name='expand'
                            toggle={true}
                            current={menuExpanded ? "expand" : null}
                        >
                            <uic-header-menu-item icon={`uic:ellipsis`} icon-only={true} name='expand' selected={menuExpanded} />
                        </uic-header-menu>
                    )}
                    {!menuCropped && <span class={`${baseClass}__spacer`} />}
                    <div class={`${baseClass}__menu-right`}>
                        <slot name='menu-right' />
                    </div>
                </nav>
                <div class={`${baseClass}__flyouts`}>
                    <slot name='flyout' />
                </div>
                <div
                    class={{
                        [`${baseClass}__submenu`]: true,
                        [`${baseClass}__submenu--hidden`]: !menuExpanded
                    }}
                    ref={(el) => (this.subMenuContainer = el)}
                >
                    {this.menuExpanded && this.renderSubmenu()}
                </div>
            </Host>
        );
    }
}
